import { useState, useCallback } from "react";
import { blogService } from "../../services/blogService";

/**
 * Custom React hook for managing blog-related state and fetching blog data.
 */
const useBlog = () => {
  // States to hold blog data and loading state
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]); // Changed to array for multiple selection
  const [singleBlog, setSingleBlog] = useState(null);
  const [singleCategory, setSingleCategory] = useState(null);
  const [blogBySlug, setBlogBySlug] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Use useCallback to prevent recreation of functions on every render
  const fetchAllBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blogService.getAllBlogs();
      setBlogs(data);
      setError(null);
      return data;
    } catch (error) {
      console.error("Error fetching all blogs:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlogsById = useCallback(async (id) => {
    try {
      setLoading(true);
      const data = await blogService.getBlogById(id);
      setSingleBlog(data);
      setError(null);
      return data;
    } catch (error) {
      console.error("Error fetching blog by ID:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlogsBySlug = useCallback(async (slug) => {
    try {
      setLoading(true);
      const data = await blogService.getBlogBySlug(slug);
      setBlogBySlug(data);
      setError(null);
      return data;
    } catch (error) {
      console.error("Error fetching blog by slug:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlogsByCategory = useCallback(async (categoryIds) => {
    try {
      setLoading(true);
      const data = await blogService.getBlogByCategory(categoryIds);
      setBlogs(data);
      setError(null);
      return data;
    } catch (error) {
      console.error("Error fetching blog by category:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blogService.getAllCategories();
      setCategories(data);
      setError(null);
      return data;
    } catch (error) {
      console.error("Error fetching all categories:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSingleCategory = useCallback(async (categoryId) => {
    try {
      setLoading(true);
      const data = await blogService.getSingleCategory(categoryId);
      setSingleCategory(data);
      setError(null);
      return data;
    } catch (error) {
      console.error("Error fetching single category:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // States
    blogs,
    categories,
    selectedCategories,
    singleBlog,
    singleCategory,
    blogBySlug,
    error,
    loading,
    // Functions
    fetchAllBlogs,
    fetchBlogsById,
    fetchBlogsBySlug,
    fetchBlogsByCategory,
    fetchAllCategories,
    fetchSingleCategory,
    setSelectedCategories,
    setSingleBlog,
    setSingleCategory,
    setBlogBySlug,
    setError,
    setLoading,
  };
};

export { useBlog };
